import type { GridSortModel, GridFilterModel, GridPaginationModel, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

// ── Context lane ──
export interface ProjectContext {
  projectId: number;
  projectName: string;
  parentProjectName?: string;
  katapultIds?: string[];
}

// ── Filter lane ──
export interface ColumnFilters {
  jobName?: string;
  project?: string;
  parentProject?: string;
  status?: string;
  owner?: string;
}

export interface QuickFilters {
  myJobs: boolean;
  readyForPickup: boolean;
}

export interface LocationFilter {
  name: string;
  bounds: { north: number; south: number; east: number; west: number };
}

// ── Views ──
export type ViewType = 'default' | 'projects' | 'construction' | 'design' | 'deeproads';

// ── Panel ──
export type PortalType = 'admin' | 'lead' | 'metrics' | 'projects' | 'qaqc' | 'construction' | 'design' | 'connectors' | 'deepfld' | 'aerial' | 'deeplearning' | 'deeptrace' | 'deeproads' | 'market-intel';

/** Per-portal metadata — replaces ad-hoc branching in UISandboxPage. */
export interface PortalDescriptor {
  type: PortalType;
  label: string;
  /** When true, the portal's expand toggle is blocked — spatial output becomes invisible. */
  requiresMapVisible: boolean;
  canCoexistWithAnalyze: boolean;
}

export const PORTAL_DESCRIPTORS: Record<PortalType, PortalDescriptor> = {
  admin:    { type: 'admin',    label: 'Admin',    requiresMapVisible: false, canCoexistWithAnalyze: true  },
  lead:     { type: 'lead',     label: 'Lead',     requiresMapVisible: false, canCoexistWithAnalyze: true  },
  metrics:  { type: 'metrics',  label: 'Metrics',  requiresMapVisible: false, canCoexistWithAnalyze: true  },
  projects: { type: 'projects', label: 'Projects', requiresMapVisible: false, canCoexistWithAnalyze: true  },
  qaqc:         { type: 'qaqc',         label: 'QA/QC',        requiresMapVisible: true,  canCoexistWithAnalyze: true  },
  construction: { type: 'construction', label: 'Construction', requiresMapVisible: false, canCoexistWithAnalyze: true  },
  design:      { type: 'design',      label: 'Design',      requiresMapVisible: false, canCoexistWithAnalyze: true  },
  connectors:  { type: 'connectors', label: 'Connectors', requiresMapVisible: false, canCoexistWithAnalyze: true  },
  deepfld:     { type: 'deepfld',    label: 'DeepFLD',    requiresMapVisible: false, canCoexistWithAnalyze: true  },
  aerial:         { type: 'aerial',        label: 'Aerial',         requiresMapVisible: false, canCoexistWithAnalyze: true  },
  deeplearning:   { type: 'deeplearning',  label: 'DeepLearning',   requiresMapVisible: false, canCoexistWithAnalyze: true  },
  deeptrace:      { type: 'deeptrace',    label: 'DeepTrace',      requiresMapVisible: false, canCoexistWithAnalyze: true  },
  deeproads:      { type: 'deeproads',    label: 'DeepRoads',      requiresMapVisible: true,  canCoexistWithAnalyze: false },
  'market-intel': { type: 'market-intel', label: 'Market Intel', requiresMapVisible: true, canCoexistWithAnalyze: true },
};

// ── Analyze session (Step 6) ──
export type AnalyzeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AnalyzeSession {
  status: AnalyzeStatus;
  scopeIds: string[];
  scopeSource: 'project' | 'selection' | 'manual' | null;
  scopeProjectName: string | null;
  inspectorOpen: boolean;
  inspectorHeight: number;
}

export type WorkspaceLayout =
  | 'default'
  | 'portal'
  | 'analyze'
  | 'portal+analyze';

// ── Spatial Overlay ──
/**
 * A discrete spatial overlay written by a portal or future tool.
 *
 * This is the ONLY write target for non-selection map highlights coming from
 * portals or analysis tools. Producers must tag their output with `source` so
 * reset logic and ConstraintChipBar can target-clear by owner.
 *
 * INVARIANT — Analyze / QAQC / design tools MUST NOT write to:
 *   - selectedIds  (triggers pole loads, cleared by Escape ×2, pollutes table)
 *   - previewIds   (engine-asymmetric: ESRI ignores previewIds entirely)
 *   - projectContext  (has typed semantics in EsriMapView highlight-clear logic)
 *
 * All jobIds are canonical store IDs (JobIdentity.id).
 * Each engine adapter calls resolveToMapId() internally — callers never pre-resolve.
 */
export interface MRPoleOverlay {
  pole_id: string;
  lat: number;
  lng: number;
  mr_type: 'Pole Replacement' | 'Power Rearrangement';
}

export interface SpatialOverlay {
  /** Which system produced this overlay — used for reset targeting and chip labels. */
  source: 'analyze' | 'qaqc' | 'design' | 'mr';
  /** Canonical store job IDs included in the overlay. */
  jobIds: string[];
  /**
   * Optional per-job RGB color tuple, keyed by canonical job ID.
   * [r, g, b] in 0–255 range. If absent for a given job, each engine
   * uses its own default overlay color.
   */
  colorMap?: Record<string, [number, number, number]>;
  /** Human-readable label for the ConstraintChipBar chip. */
  label: string;
  /** Pole-level MR overlay data — used when source='mr' to highlight individual poles. */
  mrPoles?: MRPoleOverlay[];
}

// ── Full workspace state ──
export interface WorkspaceState {
  activeView: ViewType;
  designFilter: string | null;
  projectContext: ProjectContext | null;
  searchQuery: string;
  locationFilter: LocationFilter | null;
  columnFilters: ColumnFilters;
  filterModel: GridFilterModel;
  quickFilters: QuickFilters;
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
  /**
   * Column visibility model — persisted per user.
   * Role defaults are applied once on mount; user overrides are preserved.
   * null means "not yet initialized" → triggers role-default application.
   */
  columnVisibilityModel: GridColumnVisibilityModel | null;
  /** Active grid density — persisted per user. Role-defaulted on first mount. */
  gridDensity: 'compact' | 'standard' | 'comfortable';
  /**
   * Active grid preset key — persisted per user.
   * null means no named preset is active ("Custom" — user has manually overridden columns).
   * Portal-driven presets are applied transiently (not persisted as gridPreset) to avoid
   * clobbering the user's last explicitly chosen preset.
   */
  gridPreset: string | null;
  selectedIds: string[];
  /**
   * Transient highlights before user confirmation (checkbox/map-click).
   * Also used for project-scope highlights ("Show on Map") with source='scope'.
   * Discriminated by `previewSource` — confirmPreview() is a no-op for 'scope' previews
   * so that pressing Enter after "Show on Map" does not accidentally load poles for
   * hundreds of project jobs.
   */
  previewIds: string[];
  /** Discriminates user-driven checkbox previews from portal-driven scope highlights. */
  previewSource: 'user' | 'scope';
  focusedId: string | null;
  activePortal: PortalType | null;
  portalExpanded: boolean;
  mapEngine: 'deepsync' | 'esri';
  /**
   * Isolated lane for portal / tool spatial output.
   * null means no overlay is active.
   * Cleared at Escape ×2 (same tier as selectedIds) — overlay is a map-layer
   * artifact tied to a working session, not a transient hover/focus state.
   * NOT persisted to localStorage.
   */
  spatialOverlay: SpatialOverlay | null;
  /** Active Analyze session. Orthogonal to all other workspace lanes. */
  analyzeSession: AnalyzeSession;

  // ── QA Violation Poles (Map Interaction) ──
  /** Poles with QA violations — rendered as colored rings on the map. NOT persisted. */
  violationPoles: ViolationPoleData[];
  /** Currently highlighted violation pole (clicked from analysis dialog). */
  highlightedViolationPoleId: string | null;
  /** Currently highlighted connection (clicked from midspan violation row). */
  highlightedConnectionId: string | null;

  // ── DeepTrace Target (Map → Portal launch) ──
  /** Pre-selected job+pole when DeepTrace is opened from a pole click on the map. */
  deepTraceTarget: { jobId: string; poleId: string } | null;

  // ── Make-Ready Panel (Map → MR edit) ──
  /** Pole properties for the MR editing panel. Null when no panel is open. */
  selectedPoleForMR: Record<string, any> | null;

  // ── Anchor Property Panel (Map → anchor edit) ──
  /** Anchor data for the editing panel. Null when no panel is open. */
  selectedAnchorForEdit: Record<string, any> | null;

  // ── Connection Property Panel (Map → connection edit) ──
  /** Connection data for the editing panel. Null when no panel is open. */
  selectedConnectionForEdit: Record<string, any> | null;
}

// ── Actions ──
export interface WorkspaceActions {
  setActiveView: (view: ViewType) => void;
  setDesignFilter: (filter: string | null) => void;

  setProjectContext: (ctx: ProjectContext) => void;
  clearProjectContext: () => void;

  setSearchQuery: (q: string) => void;
  clearSearch: () => void;

  setLocationFilter: (loc: LocationFilter) => void;
  clearLocationFilter: () => void;

  setColumnFilters: (f: ColumnFilters) => void;
  setFilterModel: (m: GridFilterModel) => void;
  setQuickFilters: (qf: Partial<QuickFilters>) => void;

  setSortModel: (m: GridSortModel) => void;
  setPaginationModel: (m: GridPaginationModel) => void;

  setColumnVisibilityModel: (m: GridColumnVisibilityModel) => void;
  setGridDensity: (d: 'compact' | 'standard' | 'comfortable') => void;
  setGridPreset: (key: string | null) => void;

  setSelectedIds: (ids: string[]) => void;
  setPreviewIds: (ids: string[], source?: 'user' | 'scope') => void;
  togglePreviewId: (id: string) => void;
  confirmPreview: () => void;
  setFocusedId: (id: string | null) => void;
  /**
   * Atomic "Show on Map" state update for ProjectsPortal.
   * Sets projectContext (scope chip) + previewIds with source='scope' (yellow table highlights).
   * Does NOT call fitToJobs — the caller is responsible for the zoom command so that
   * status colors (computed outside the store) can be forwarded in the same call.
   * INVARIANT: does not mutate selectedIds.
   */
  showProjectOnMap: (context: ProjectContext) => void | Promise<void>;

  setActivePortal: (p: PortalType | null) => void;
  setPortalExpanded: (v: boolean) => void;
  setMapEngine: (e: 'deepsync' | 'esri') => void;

  resetFocusAndPreview: () => void;
  resetSelection: () => void;
  resetWorkspace: () => void;

  /** Set or replace the active spatial overlay. Pass a fully constructed SpatialOverlay. */
  setSpatialOverlay: (overlay: SpatialOverlay) => void;
  /** Remove the active spatial overlay regardless of source. */
  clearSpatialOverlay: () => void;

  // ── Analyze Session (Step 6) ──
  startAnalyze: (scopeIds: string[], scopeSource: AnalyzeSession['scopeSource'], projectName?: string) => void;
  closeAnalyze: () => void;
  setAnalyzeInspector: (open: boolean) => void;
  setAnalyzeInspectorHeight: (h: number) => void;

  /** Returns true if the active portal can expand to full-screen (map hidden). */
  canExpandPortal: () => boolean;

  // ── QA Violation Poles (Map Interaction) ──
  setViolationPoles: (poles: ViolationPoleData[]) => void;
  setHighlightedViolationPoleId: (id: string | null) => void;
  setHighlightedConnectionId: (id: string | null) => void;

  // ── DeepTrace Target ──
  /** Open DeepTrace portal pre-focused on a specific job+pole. */
  openDeepTrace: (jobId: string, poleId: string) => void;
  clearDeepTraceTarget: () => void;

  // ── Make-Ready Panel ──
  selectPoleForMR: (pole: Record<string, any>) => void;
  clearPoleForMR: () => void;

  // ── Anchor Property Panel ──
  selectAnchorForEdit: (anchor: Record<string, any>) => void;
  clearAnchorForEdit: () => void;

  // ── Connection Property Panel ──
  selectConnectionForEdit: (conn: Record<string, any>) => void;
  clearConnectionForEdit: () => void;
}

// ── Violation Poles (QA Map Interaction) ──
export interface ViolationPoleData {
  poleId: string;           // katapult node id (matches pole feature's katapult_id property)
  lat: number;
  lng: number;
  mrType: string;
  violationCount: number;
  violationSummary: string[]; // e.g. ["Height violation: Clearnetworx fiber too low", ...]
  hasMrContext: boolean;    // true when ≥1 violation on this pole has an existing (insufficient) MR move
}

// ── Job Identity ──
export interface JobIdentity {
  id: string;
  katapultId: string;
  jobName: string;
  firebaseId?: string;
  /** Workflow status label (e.g. 'In Progress', 'Delivered'). Populated by setJobs(). */
  status?: string;
  /** RGB color tuple derived from the status. Used by fitToJobs for color-coded highlights. */
  statusColor?: [number, number, number];
}

export interface JobIdentityState {
  identities: Map<string, JobIdentity>;
  setJobs: (rows: any[]) => void;
  resolve: (unknownId: string) => JobIdentity | undefined;
  resolveToMapId: (id: string, engine: 'deepsync' | 'esri') => string | undefined;
  resolveFromMapId: (mapId: string, engine: 'deepsync' | 'esri') => string | undefined;
  resolveMany: (ids: string[], engine: 'deepsync' | 'esri') => string[];
  /** Build a per-job color map from stored status metadata. IDs are canonical store IDs. */
  getJobColors: (ids: string[]) => Record<string, [number, number, number]>;
}
